import { SendProposal } from './models/send-proposal';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { environment } from 'src/environments/environment';
import { map, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { UiStateStore } from '../shared/store/ui-state-store.service';
import { JobSummary } from './models/job-summary';
import { JobFilter } from './models/job-filter';
import { SearchResult } from '../shared/models/search-result';
import { JobApplication } from './models/job-application';
import { Profile } from '../shared/models/profile';
import { JobBookmark } from './models/job-bookmark';
import { JobPreview } from '../dashboards/models/job-preview';
import { AppliedJob } from './models/applied-job';
import { MyJobStatuses } from './models/job-statuses';
import { JobStatusCount } from './models/job-status-count';
import { JobInfo } from './models/job-info';
import { ChatService } from '../messages/services/chat.service';
import { AppStore } from '../shared/store/lookup-store.service';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  constructor(private httpClient: HttpClient, private chatService: ChatService, private lookupStore: AppStore, private uiState: UiStateStore) { }

  public searchJobs(
    searchKeyword: string,
    location: string
  ): Observable<JobSummary[]> {
    const url = `${environment.baseApiUrl}/jobs/search/${searchKeyword}/locations/${location}`;
    return this.httpClient.get<JobSummary[]>(url).pipe(
      map((response) => {
        return response.map((resp) => JobSummary.parse(resp));
      }),
      catchError((err) => {
        this.uiState.setError(`Error when searching jobs: ${err['message']}`);
        return throwError(err);
      })
    );
  }

  public filterJobs(
    jobsFilter: JobFilter
  ): Observable<SearchResult<JobSummary>> {
    const url = `${environment.baseApiUrl}/jobs/search/filter`;
    return this.httpClient.post<SearchResult<JobSummary>>(url, jobsFilter).pipe(
      map((response) => {
        return SearchResult.parse<JobSummary>(response, new JobSummary());
      }),
      catchError((err) => {
        this.uiState.setError(`Error when filtering jobs: ${err['message']}`);
        return throwError(err);
      })
    );
  }

  public applyJob(jobApplication: JobApplication): Observable<JobApplication> {
    const url = `${environment.baseApiUrl}/jobs/apply`;
    return this.httpClient.post<JobApplication>(url, jobApplication).pipe(
      map((response) => {
        this.uiState.setSuccess(
          `Your have successfully applied for ${jobApplication.jobTitle}`
        );
        return response as JobApplication;
      }),
      catchError((err) => {
        this.uiState.setError(`Error when applying for job: ${err['message']}`);
        return throwError(err);
      })
    );
  }
  public sendJobProposal(sendProposal: SendProposal): Observable<SendProposal> {
    const url = `${environment.baseApiUrl}/jobs/sendProposal`;
    return this.httpClient.post<SendProposal>(url, sendProposal).pipe(
      map((response) => {
        this.uiState.setSuccess(
          `Your have successfully send proposal for job  ${sendProposal.jobId} `
        );
        let salaryType = this.lookupStore.getSalaryType(sendProposal.salaryType);
        this.chatService.send(SendProposal.convertToChatMessage(sendProposal, salaryType));
        return response as SendProposal;
      }),
      catchError((error) => {
        this.uiState.setError(
          `Error when sending proposal for job: ${error['message']}`
        );
        return throwError(() => new Error(error));
      })
    );
  }
  public getJobBookmarks(accountId: string): Observable<JobBookmark[]> {
    const url = `${environment.baseApiUrl}/jobs/bookmarks/${accountId}`;
    return this.httpClient.get<JobBookmark[]>(url).pipe(
      map((response) => {
        return response;
      }),
      catchError((err) => {
        this.uiState.setError(
          `Error when getting job bookmarks: ${err['message']}`
        );
        return throwError(err);
      })
    );
  }

  public bookmarkJob(jobBookmark: JobBookmark): Observable<JobBookmark> {
    const url = `${environment.baseApiUrl}/jobs/bookmark`;
    return this.httpClient.post<JobApplication>(url, jobBookmark).pipe(
      map((response) => {
        this.uiState.setSuccess(`Your have successfully bookmarked this job.`);
        return response as JobApplication;
      }),
      catchError((err) => {
        this.uiState.setError(
          `Error when bookmarking this job: ${err['message']}`
        );
        return throwError(err);
      })
    );
  }

  public unBookmarkJob(jobBookmark: JobBookmark): Observable<JobBookmark> {
    const url = `${environment.baseApiUrl}/jobs/unbookmark`;
    return this.httpClient.post<JobApplication>(url, jobBookmark).pipe(
      map((response) => {
        this.uiState.setSuccess(
          `Your have successfully unbookmarked this job.`
        );
        return response as JobApplication;
      }),
      catchError((err) => {
        this.uiState.setError(
          `Error when unbookmarking this job: ${err['message']}`
        );
        return throwError(err);
      })
    );
  }

  public getProfile(accountId: string): Observable<Profile> {
    const url = `${environment.baseApiUrl}/profiles/getByAccountId/${accountId}`;
    return this.httpClient.get<Profile>(url).pipe(
      map((response) => {
        return Profile.parse(response);
      }),
      catchError((err) => {
        this.uiState.setError(
          `Error when requesting profile: ${err['message']}`
        );
        return throwError(err);
      })
    );
  }

  public getJobPreview(
    accountId: string,
    jobId: string
  ): Observable<JobPreview> {
    let url = '';

    if (accountId) {
      url = `${environment.baseApiUrl}/jobs/job-preview/${accountId}/${jobId}`;
    } else {
      url = `${environment.baseApiUrl}/jobs/job-preview/${jobId}`;
    }

    return this.httpClient.get<JobPreview>(url).pipe(
      map((response) => {
        return JobPreview.parse(response);
      }),
      catchError((err) => {
        this.uiState.setError(
          `Error when getting JobPreview: ${err['message']}`
        );
        return throwError(err);
      })
    );
  }

  public getAppliedJobs(accountId: string): Observable<AppliedJob[]> {
    const url = `${environment.baseApiUrl}/jobs/appliedjobs/${accountId}`;
    return this.httpClient.get<AppliedJob[]>(url).pipe(
      map((response) => {
        return response;
      }),
      catchError((err) => {
        this.uiState.setError(
          `Error when getting applied jobs: ${err['message']}`
        );
        return throwError(err);
      })
    );
  }

  public getMyAppliedJobs(accountId: string): Observable<JobSummary[]> {
    const url = `${environment.baseApiUrl}/jobs/myappliedjobs/${accountId}`;
    return this.httpClient.get<JobSummary[]>(url).pipe(
      map((response) => {
        return response;
      }),
      catchError((err) => {
        this.uiState.setError(
          `Error when getting my applied jobs: ${err['message']}`
        );
        return throwError(err);
      })
    );
  }

  getMyJobs(
    status: MyJobStatuses,
    accountId: string,
    pageNumber: number
  ): Observable<SearchResult<JobSummary>> {
    const url = `${environment.baseApiUrl}/jobs/myjobs/${status}/${accountId}/${pageNumber}`;
    return this.httpClient.get<SearchResult<JobSummary>>(url).pipe(
      map((response) => {
        return SearchResult.parse<JobSummary>(response, new JobSummary())
      }),
      catchError((err) => {
        this.uiState.setError(`Error when getting my jobs: ${err['message']}`);
        return throwError(err);
      })
    );
  }

  getMyJobIds(
    status: MyJobStatuses,
    accountId: string
  ): Observable<string[]> {
    const url = `${environment.baseApiUrl}/jobs/myjobIds/${status}/${accountId}`;
    return this.httpClient.get<string[]>(url).pipe(
      map((response) => {
        return response;
      }),
      catchError((err) => {
        this.uiState.setError(`Error when getting my jobIds: ${err['message']}`);
        return throwError(err);
      })
    );
  }

  declineJobInvite(jobPostId: any, candidateAccountId: string): Observable<void> {
    const url = `${environment.baseApiUrl}/candidates/${candidateAccountId}/decline/${jobPostId}`;
    return this.httpClient.get(url)
      .pipe(map(() => {
        return;
      }),
        catchError(err => {
          this.uiState.setError(`Error when getting Jobs: ${err["message"]}`);
          return throwError(err);
        }));
  }

  getMyJobsStatusCount(accountId: string): Observable<JobStatusCount> {
    const url = `${environment.baseApiUrl}/jobs/myjobs/${accountId}/statuscounts`;
    return this.httpClient.get<JobStatusCount>(url).pipe(
      map((response) => {
        return response;
      }),
      catchError((err) => {
        this.uiState.setError(
          `Error when getting my jobs status count: ${err['message']}`
        );
        return throwError(err);
      })
    );
  }

  public getLatestJobs(): Observable<JobSummary[]> {
    const url = `${environment.baseApiUrl}/jobs/latest-jobs`;
    return this.httpClient.get<JobSummary[]>(url).pipe(
      map((response) => {
        return response.map((resp) => JobSummary.parse(resp));
      }),
      catchError((err) => {
        this.uiState.setError(
          `Error when getting latest jobs: ${err['message']}`
        );
        return throwError(err);
      })
    );
  }

  getJobInfo(jobId: string | null): Observable<JobInfo> {
    const url = `${environment.baseApiUrl}/jobs/jobInfo/${jobId}`;
    return this.httpClient.get<JobInfo>(url).pipe(
      map((response) => {
        return JobInfo.parse(response);
      }),
      catchError((err) => {
        this.uiState.setError(`Error when getting job info: ${err['message']}`);
        return throwError(err);
      })
    );
  }
}
